import type {
  CreatePrinterRequest,
  PrinterAdminRow,
  UpdatePrinterRequest,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

/**
 * Admin printer-fleet client. Talks to the admin CRUD endpoints on
 * `/api/printers` (SWA-header admin auth) — NOT the agent's bearer-auth
 * `GET /api/printers/{siteId}`.
 */
export async function fetchPrinters(
  signal?: AbortSignal,
  options?: { siteId?: string; includeInactive?: boolean },
): Promise<PrinterAdminRow[]> {
  const params = new URLSearchParams();
  if (options?.siteId) params.set('siteId', options.siteId);
  // Admin default is to include inactive; only send the flag when opting out.
  if (options?.includeInactive === false) params.set('includeInactive', 'false');
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE_URL}/printers${query}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch printers: ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  return data.items ?? [];
}

export async function createPrinter(
  payload: CreatePrinterRequest,
  signal?: AbortSignal,
): Promise<PrinterAdminRow> {
  const response = await fetch(`${API_BASE_URL}/printers`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to create printer: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function updatePrinter(
  printerId: string,
  payload: UpdatePrinterRequest,
  signal?: AbortSignal,
): Promise<PrinterAdminRow> {
  const response = await fetch(`${API_BASE_URL}/printers/${printerId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to update printer: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
  return response.json();
}

export async function deletePrinter(
  printerId: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/printers/${printerId}`, {
    method: 'DELETE',
    signal,
  });
  if (!response.ok) {
    const err = await response.json().catch(() => ({
      error: `Failed to delete printer: ${response.status}`,
    }));
    throw new Error(err.error || `HTTP ${response.status}`);
  }
}
